/**
 * Auth store (zustand) — token + user persisted to localStorage.
 *
 * I18N-02: on login, ``user.preferred_lang`` is applied via ``i18n.changeLanguage``
 * AND saved to ``localStorage["talenta.lang"]`` so the language preference survives
 * a fresh tab.
 *
 * USR-04: ``user.password_must_rotate`` is consumed by ``ProtectedRoute`` to bounce
 * the user to ``/rotate-password`` on every navigation until they rotate.
 *
 * Hydration: on app boot, ``hydrate()`` is called once from ``App.tsx``. If a token
 * is in localStorage, it fires ``GET /auth/me`` to validate; on 401, state is cleared.
 * Dynamic ``import("@/lib/api")`` breaks the api↔auth circular import.
 */
import { create } from "zustand";
import i18n from "@/lib/i18n";
import type { Lang, UserPublic } from "@/types/api";

const STORAGE_KEY = "talenta.auth";
const LANG_STORAGE_KEY = "talenta.lang";

interface AuthState {
  token: string | null;
  user: UserPublic | null;
  login: (token: string, user: UserPublic) => void;
  logout: () => void;
  setLanguage: (lang: Lang) => void;
  hydrate: () => void;
}

function loadFromStorage(): { token: string | null; user: UserPublic | null } {
  if (typeof window === "undefined") return { token: null, user: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { token: null, user: null };
    const parsed = JSON.parse(raw) as { token?: string; user?: UserPublic };
    return { token: parsed.token ?? null, user: parsed.user ?? null };
  } catch {
    return { token: null, user: null };
  }
}

function persist(token: string | null, user: UserPublic | null) {
  if (typeof window === "undefined") return;
  if (token && user) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

export const useAuthStore = create<AuthState>((set, get) => {
  const initial = loadFromStorage();
  return {
    token: initial.token,
    user: initial.user,
    login: (token, user) => {
      persist(token, user);
      // I18N-02: apply preferred_lang on login + persist for next session.
      i18n.changeLanguage(user.preferred_lang);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LANG_STORAGE_KEY, user.preferred_lang);
      }
      set({ token, user });
    },
    logout: () => {
      persist(null, null);
      set({ token: null, user: null });
    },
    setLanguage: (lang) => {
      const u = get().user;
      if (u) {
        const nextUser = { ...u, preferred_lang: lang };
        persist(get().token, nextUser);
        set({ user: nextUser });
      }
      i18n.changeLanguage(lang);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LANG_STORAGE_KEY, lang);
      }
    },
    hydrate: () => {
      // Verify any persisted token by hitting /auth/me. Dynamic import breaks the
      // api.ts <-> auth.ts cycle (api.ts reads useAuthStore for the token).
      const token = get().token;
      if (!token) return;
      void import("@/lib/api").then(({ apiFetch }) =>
        apiFetch<UserPublic>("/auth/me")
          .then((freshUser) => {
            persist(get().token, freshUser);
            set({ user: freshUser });
          })
          .catch(() => {
            // 401 already routed by apiFetch; this catch ensures local state aligns
            // even when the redirect is suppressed (e.g. in tests).
            persist(null, null);
            set({ token: null, user: null });
          }),
      );
    },
  };
});
